import React, { useState, useContext } from 'react';
import styled from 'styled-components';

import Dropzone from '../../misc/Dropzone';
import Button from '../../misc/Button';
import Icon from '../../misc/Icon';

import InputSettingsField from './InputSettingsField';
import CheckboxSettingsField from './CheckboxSettingsField';
import IdentifierSelectorSettings from './IdentifierSelectorSettings';

import FieldStyleSettings from './FieldStyleSettings';
import PositionSettings from './PositionSettings';
import MetadataSettings from './MetadataSettings';
import ButtonGroupSettings from './ButtonGroupSettings';
import ConditionalLogicSettings from './ConditionalLogicSettings';

import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import SettingsTabs from './SettingsTabs';
import TooltipSettings from './TooltipSettings';
import AccordionSettings from './AccordionSettings';
import SettingsLabel from './SettingsLabel';
import TitleSettings from './TitleSettings';
import { StyledFieldWrapper, StyledButtonsWrapper, DividerLine } from './SettingsCommonElements';

import FieldTypes from '../../../constants/FieldTypes';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import { isSettingHidden, isStyleSettingHidden } from '../../../lib/settingsHelper';

const StyledWrapper = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
`;

const StyledUploadWrapper = styled.div`
  margin: 0px 6px 12px 6px;
`;

const StyledFieldWrapperRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin: 0px 12px 12px 12px;
`;

const FieldSettingsImageField = ({

  //Logic properties
  fileId,
  sortedPages,
  fields,
  logic,

  fieldId,
  fieldIdentifier,
  fieldPositionId,

  view,
  title,
  value,
  metadata,
  config,
  type,
  multi,
  required,
  hidden,
  disabled,

  x,
  y,
  width,
  height,

  //Title styles
  titleFontSize,
  titleFontColor,
  titleTextAlign,
  titleFontStyle,
  titleFontWeight,
  titleTextTransform,
  titleTextDecoration,

  //API Developers
  identifier,

  titleDisplay,
  borderColor,
  borderRadius,
  borderWidth,
  padding,
  margin,
  zIndex,

  //Action handlers
  onChange,
  onDelete,
  onUpload,

  primaryMaxHeight,
  primaryMaxWidth,

  tipTitle,
  tipDescription,
  tipVisible,

}) => {

  const theme = useContext(ThemeContext);
  const [customImageWidthEnabled, setCustomImageWidthEnabled] = useState(!!primaryMaxWidth || !!primaryMaxHeight);

  const isTitleHidden = isSettingHidden(config, 'title');
  const isStylesHidden = isSettingHidden(config, 'styles');
  const isDisabledHidden = isSettingHidden(config, 'disabled');
  const isRequiredHidden = isSettingHidden(config, 'required');
  const isMultiHidden = isSettingHidden(config, 'multi');
  const isHiddenHidden = isSettingHidden(config, 'hidden');
  const isLogicHidden = isSettingHidden(config, 'logic');
  const isTooltipHidden = isSettingHidden(config, 'tooltip');
  const isIdentifierHidden = isSettingHidden(config, 'identifier');
  const isDeleteHidden = isSettingHidden(config, 'delete');
  const isUploadHidden = isSettingHidden(config, 'upload');

  //Title Styles
  const isTitleFontSizeHidden = isStyleSettingHidden(config, 'titleFontSize');
  const isTitleFontColorHidden = isStyleSettingHidden(config, 'titleFontColor');
  const isTitleTextAlignHidden = isStyleSettingHidden(config, 'titleTextAlign');
  const isTitleFontStyleHidden = isStyleSettingHidden(config, 'titleFontStyle');
  const isTitleFontWeightHidden = isStyleSettingHidden(config, 'titleFontWeight');
  const isTitleTextTransformHidden = isStyleSettingHidden(config, 'titleTextTransform');
  const isTitleTextDecorationHidden = isStyleSettingHidden(config, 'titleTextDecoration');

  //Field Styles
  const isBorderColorHidden = isStyleSettingHidden(config, 'borderColor');
  const isBorderRadiusHidden = isStyleSettingHidden(config, 'borderRadius');
  const isBorderWidthHidden = isStyleSettingHidden(config, 'borderWidth');
  const isBackgroundColorHidden = isStyleSettingHidden(config, 'backgroundColor');
  const isMarginHidden = isStyleSettingHidden(config, 'margin');
  const isPaddingHidden = isStyleSettingHidden(config, 'padding');
  const isZIndexHidden = isStyleSettingHidden(config, 'zIndex');
  const isPositionHidden = isStyleSettingHidden(config, 'position');

  const stylesNode = (
    <>
      {(isTitleFontSizeHidden
        && isTitleFontColorHidden
        && isTitleTextAlignHidden
        && isTitleFontStyleHidden
        && isTitleFontWeightHidden
        && isTitleTextTransformHidden
        && isTitleTextDecorationHidden) ? null : (
          <>
            <AccordionSettings
              isOpen
              title="Title"
              height={170}
            >
              <FieldStyleSettings
                id={`${fieldId}-title`}
                {...{
                  fontSize: titleFontSize,
                  onFontSizeChange: isTitleFontSizeHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontSize: value }),

                  fontColor: titleFontColor,
                  onFontColorChange: isTitleFontColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontColor: value }),

                  textAlign: titleTextAlign,
                  onTextAlignChange: isTitleTextAlignHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextAlign: value }),

                  fontStyle: titleFontStyle,
                  onFontStyleChange: isTitleFontStyleHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontStyle: value }),

                  fontWeight: titleFontWeight,
                  onFontWeightChange: isTitleFontWeightHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontWeight: value }),

                  textTransform: titleTextTransform,
                  onTextTransformChange: isTitleTextTransformHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextTransform: value }),

                  textDecoration: titleTextDecoration,
                  onTextDecorationChange: isTitleTextDecorationHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextDecoration: value }),
                }}
              />
            </AccordionSettings>
            <DividerLine />
          </>
        )}
      {(
        isBorderColorHidden
         && isBorderRadiusHidden
         && isBorderWidthHidden
         && isBackgroundColorHidden
         && isMarginHidden
         && isPaddingHidden
         && isPositionHidden
      ) ? null : (
        <AccordionSettings
          isOpen
          title="Field"
          height={340}
        >
          <FieldStyleSettings
            id={`${fieldId}-field`}
            {...{

              borderColor,
              onBorderColorChange: isBorderColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderColor: value }),

              borderRadius,
              onBorderRadiusChange: isBorderRadiusHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderRadius: value }),

              borderWidth,
              onBorderWidthChange: isBorderWidthHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderWidth: value }),

              padding,
              onPaddingChange: isPaddingHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { padding: value }),

              margin,
              onMarginChange: isMarginHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { margin: value }),

              zIndex,
              onZIndexChange: isZIndexHidden ? null : (value) => (value ? onChange(fieldId, fieldIdentifier, fieldPositionId, { zIndex: value }) : ''),

            }}
          />
          {isPositionHidden ? null : (
            <PositionSettings
              {...{
                view,
                x,
                y,
                width,
                height,
                onChange: (field, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { [field]: value })
              }}
            />
          )}
        </AccordionSettings>
        )}
    </>
  );

  /**
   * Logic below allows any file type for the file field
   */
  let acceptedFileTypes = {};

  if (type === FieldTypes.image) {

    /**
     * Logic below only allows images for the image field
     */
    acceptedFileTypes = {
      'image/jpeg': ['.jpg', '.jpeg', '.JPG', '.JPEG'],
      'image/png': ['.png', '.PNG'],
    };

  }

  const settingsNode = (
    <>
      {isTitleHidden ? null : (
        <>
          <SettingsLabel>Title</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <TitleSettings
            {...{
              title,
              titleDisplay
            }}
            onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
          />
        </>
      )}
      {(isMultiHidden
        && isUploadHidden
        && isRequiredHidden
        && isDisabledHidden
        && isHiddenHidden) ? null : (
          <>
            <SettingsLabel>Field</SettingsLabel>
            <DividerLine style={{ marginBottom: 14 }} />
          </>
        )}
      {isUploadHidden ? null : (
        <>
          <StyledUploadWrapper>
            <Dropzone
              multiple={multi}
              dragMessage=" "
              styles={{ padding: '8px', fontSize: '12px', borderRadius: '6px' }}
              onDrop={(files) => {
                onUpload(fieldId, fieldIdentifier, fieldPositionId, files);
              }}
              accept={acceptedFileTypes}
            />
          </StyledUploadWrapper>
          {!value ? null : (
            <StyledButtonsWrapper>
              <Button
                danger
                icon={ThemeIcons.settings.delete}
                text={type === FieldTypes.file ? 'File(s)' : 'Image(s)'}
                onClick={() => onChange(fieldId, fieldIdentifier, fieldPositionId, { value: '' })}
              />
            </StyledButtonsWrapper>
          )}
        </>
      )}
      {isMultiHidden ? null : (
        <StyledFieldWrapper>
          <CheckboxSettingsField
            id="multi"
            icon={ThemeIcons.settings.multiImages}
            iconColor={multi ? theme.primaryColor : theme.borderColor}
            label={type === FieldTypes.file ? 'Multiple Files' : 'Multiple Images'}
            selected={multi}
            onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { multi: selected },)}
          />
        </StyledFieldWrapper>
      )}
      {!multi || type === FieldTypes.file ? null : (
        <>
          <StyledFieldWrapper style={{ marginBottom: 0 }}>
            <ButtonGroupSettings
              label="Image Size"
              types={[
                {
                  type: 'auto',
                  label: 'Auto',
                  active: !customImageWidthEnabled,
                },
                {
                  type: 'custom',
                  label: 'Custom',
                  active: customImageWidthEnabled,
                }
              ]}
              onChange={(value) => {
                if (value === 'custom') {
                  setCustomImageWidthEnabled(true);
                } else if (value === 'auto') {
                  setCustomImageWidthEnabled(false);
                  onChange(fieldId, fieldIdentifier, fieldPositionId, { primaryMaxWidth: 0, primaryMaxHeight: 0 });
                }
              }}
            />
          </StyledFieldWrapper>
          {!customImageWidthEnabled ? null : (
            <>
              <StyledFieldWrapperRow>
                <StyledFieldWrapper style={{ maxWidth: '50%', paddingRight: '4px', margin: '0px' }}>
                  <InputSettingsField
                    value={primaryMaxWidth}
                    addonBefore={<Icon icon={ThemeIcons.settings.primaryWidth} size="sm" />}
                    type="number"
                    onChange={(i, value) => {
                      onChange(fieldId, fieldIdentifier, fieldPositionId, { primaryMaxWidth: value ? parseFloat(value, 10) : 0 });
                    }}
                  />
                </StyledFieldWrapper>
                <StyledFieldWrapper style={{ maxWidth: '50%', paddingLeft: '4px', margin: '0px' }}>
                  <InputSettingsField
                    value={primaryMaxHeight}
                    addonBefore={<Icon icon={ThemeIcons.settings.primaryHeight} size="sm" />}
                    type="number"
                    onChange={(i, value) => {
                      onChange(fieldId, fieldIdentifier, fieldPositionId, { primaryMaxHeight: value ? parseFloat(value, 10) : 0 });
                    }}
                  />
                </StyledFieldWrapper>
              </StyledFieldWrapperRow>
            </>
          )}
        </>
      )}
      {isDisabledHidden ? null : (
        <StyledFieldWrapper>
          <CheckboxSettingsField
            icon={ThemeIcons.settings.disabled}
            iconColor={disabled ? theme.primaryColor : theme.borderColor}
            id="disabled"
            label="Read-only"
            selected={disabled}
            onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { disabled: selected },)}
          />
        </StyledFieldWrapper>
      )}
      {isRequiredHidden ? null : (
        <StyledFieldWrapper>
          <CheckboxSettingsField
            icon={ThemeIcons.settings.required}
            iconColor={required ? theme.dangerColor : theme.borderColor}
            iconSize="1x"
            id="required"
            label="Required"
            selected={required}
            onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { required: selected },)}
          />
        </StyledFieldWrapper>
      )}
      {isHiddenHidden ? null : (
        <StyledFieldWrapper>
          <CheckboxSettingsField
            icon={ThemeIcons.settings.hide}
            iconColor={hidden ? theme.warningColor : theme.borderColor}
            id="hidden"
            label="Hidden"
            selected={hidden}
            onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { hidden: selected },)}
          />
        </StyledFieldWrapper>
      )}
      {isLogicHidden || fields.length < 2 ? null : (
        <>
          <DividerLine />
          <StyledFieldWrapper>
            <ConditionalLogicSettings
              fileId={fileId}
              targetLabel=" field"
              targetFieldId={fieldId}
              sortedPages={sortedPages}
              fields={fields}
              logic={logic}
              onSubmit={(nextLogic) => onChange(fieldId, fieldIdentifier, fieldPositionId, { logic: nextLogic })}
            />
          </StyledFieldWrapper>
        </>
      )}
      {!config?.metadata ? null : (
        <>
          <DividerLine />
          <MetadataSettings
            metadata={metadata}
            metadataOptions={config?.metadata}
            onChange={(changes) => {
              const nextMetadata = metadata ? { ...metadata, ...changes } : { ...changes };
              onChange(fieldId, fieldIdentifier, fieldPositionId, { metadata: nextMetadata });
            }}
          />
        </>
      )}
      {isTooltipHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title="Tooltip"
            height={166}
          >
            <TooltipSettings
              {...{
                tipVisible,
                tipTitle,
                tipDescription
              }}
              onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
            />
          </AccordionSettings>
        </>
      )}
      {isIdentifierHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title={config?.identifier?.label || 'Identifier'}
            height={60}
          >
            <IdentifierSelectorSettings
              fieldId={fieldId}
              fieldType={type}
              type="text"
              customValue={config?.identifier?.options}
              enableCustomOption={config?.identifier?.custom}
              value={identifier}
              onChange={(fieldId, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { identifier: value })}
            />
          </AccordionSettings>
        </>
      )}
      {isDeleteHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 14 }} />
          <StyledButtonsWrapper>
            <Button
              danger
              icon={ThemeIcons.settings.delete}
              text="Delete"
              onClick={() => onDelete(fieldId, fieldIdentifier, fieldPositionId)}
            />
          </StyledButtonsWrapper>
        </>
      )}
    </>
  );

  return (
    <StyledWrapper data-testid="form-settings">
      <FieldSettingsHeaderTitle
        title="Settings"
        icon={type === FieldTypes.file ? ThemeIcons.fields.file : ThemeIcons.fields.image}
      />
      <SettingsTabs
        settings={settingsNode}
        styles={isStylesHidden ? null : stylesNode}
      />
    </StyledWrapper>
  );

};

export default FieldSettingsImageField;
